// ChannelNotificationPrefService.js

// Firebase
import { deleteDoc, doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

// Utilities
import { withBaseKey } from 'utilities/basePersistence';

const BASES_COLLECTION = collections.bases;

export const CHANNEL_NOTIFICATION_MODES = Object.freeze({
  ALL: 'all',
  MENTIONS: 'mentions',
  MUTE: 'mute',
});

export const DEFAULT_CHANNEL_NOTIFICATION_MODE = CHANNEL_NOTIFICATION_MODES.MENTIONS;

export const CHANNEL_NOTIFICATION_MODE_OPTIONS = Object.freeze([
  {
    mode: CHANNEL_NOTIFICATION_MODES.ALL,
    label: 'All new posts',
    note: 'Notify for every new message in this channel.',
  },
  {
    mode: CHANNEL_NOTIFICATION_MODES.MENTIONS,
    label: 'Just mentions',
    note: 'Notify when you are mentioned or someone replies to you.',
  },
  {
    mode: CHANNEL_NOTIFICATION_MODES.MUTE,
    label: 'Mute',
    note: 'No notifications from this channel.',
  },
]);

/**
 * Normalizes a stored mode to a known preset.
 *
 * @param {*} value
 * @returns {string}
 */
export function normalizeChannelNotificationMode(value) {
  const mode = String(value || '').trim().toLowerCase();
  if (mode === CHANNEL_NOTIFICATION_MODES.ALL) return CHANNEL_NOTIFICATION_MODES.ALL;
  if (mode === CHANNEL_NOTIFICATION_MODES.MUTE) return CHANNEL_NOTIFICATION_MODES.MUTE;
  if (mode === CHANNEL_NOTIFICATION_MODES.MENTIONS) return CHANNEL_NOTIFICATION_MODES.MENTIONS;
  return DEFAULT_CHANNEL_NOTIFICATION_MODE;
}

/**
 * @param {string} baseKey
 * @param {string} channelKey
 * @param {string} userKey
 * @returns {import('firebase/firestore').DocumentReference|null}
 */
export function channelNotificationPrefDocRef(baseKey, channelKey, userKey) {
  const cleanBaseKey = String(baseKey || '').trim();
  const cleanChannelKey = String(channelKey || '').trim();
  const cleanUserKey = String(userKey || '').trim();
  if (!cleanBaseKey || !cleanChannelKey || !cleanUserKey) return null;

  return doc(
    db,
    BASES_COLLECTION,
    cleanBaseKey,
    'channels',
    cleanChannelKey,
    'notificationPreferences',
    cleanUserKey
  );
}

/**
 * Legacy Follow doc (treated as mode `all` when no preference exists).
 *
 * @param {string} baseKey
 * @param {string} channelKey
 * @param {string} userKey
 * @returns {import('firebase/firestore').DocumentReference|null}
 */
function channelFollowDocRef(baseKey, channelKey, userKey) {
  const cleanBaseKey = String(baseKey || '').trim();
  const cleanChannelKey = String(channelKey || '').trim();
  const cleanUserKey = String(userKey || '').trim();
  if (!cleanBaseKey || !cleanChannelKey || !cleanUserKey) return null;

  return doc(
    db,
    BASES_COLLECTION,
    cleanBaseKey,
    'channels',
    cleanChannelKey,
    'follows',
    cleanUserKey
  );
}

/**
 * Sets the per-channel notification preset for the current user.
 *
 * @param {object} params
 * @param {string} params.baseKey
 * @param {string} params.channelKey
 * @param {string} params.userKey
 * @param {string} params.mode
 * @returns {Promise<void>}
 */
export async function setChannelNotificationMode({
  baseKey,
  channelKey,
  userKey,
  mode,
} = {}) {
  const ref = channelNotificationPrefDocRef(baseKey, channelKey, userKey);
  if (!ref) {
    throw new Error('setChannelNotificationMode requires baseKey, channelKey, and userKey.');
  }

  const cleanBaseKey = String(baseKey || '').trim();
  const cleanChannelKey = String(channelKey || '').trim();
  const cleanUserKey = String(userKey || '').trim();
  const cleanMode = normalizeChannelNotificationMode(mode);

  await setDoc(
    ref,
    withBaseKey({
      userKey: cleanUserKey,
      baseKey: cleanBaseKey,
      channelKey: cleanChannelKey,
      mode: cleanMode,
      updatedAt: serverTimestamp(),
      updatedBy: cleanUserKey,
    }, cleanBaseKey),
    { merge: true }
  );

  // Drop legacy follow doc once a real preference exists.
  const legacyRef = channelFollowDocRef(baseKey, channelKey, userKey);
  if (legacyRef) {
    try {
      await deleteDoc(legacyRef);
    } catch (_error) {
      // ignore
    }
  }
}

/**
 * Best-effort cleanup when leaving a channel.
 *
 * @param {object} params
 * @param {string} params.baseKey
 * @param {string} params.channelKey
 * @param {string} params.userKey
 * @returns {Promise<void>}
 */
export async function clearChannelNotificationPref({ baseKey, channelKey, userKey } = {}) {
  const refs = [
    channelNotificationPrefDocRef(baseKey, channelKey, userKey),
    channelFollowDocRef(baseKey, channelKey, userKey),
  ].filter(Boolean);

  await Promise.all(refs.map(async (ref) => {
    try {
      await deleteDoc(ref);
    } catch (error) {
      if (error?.code === 'not-found' || error?.code === 'permission-denied') {
        return;
      }
      console.warn('[ChannelNotificationPrefService] clear failed:', error);
    }
  }));
}

/**
 * Subscribes to the viewer's notification mode for a channel.
 *
 * @param {string} baseKey
 * @param {string} channelKey
 * @param {string} userKey
 * @param {function(string): void} callback - Receives normalized mode.
 * @param {function(Error): void} [onError]
 * @returns {function(): void}
 */
export function subscribeToChannelNotificationMode(
  baseKey,
  channelKey,
  userKey,
  callback,
  onError
) {
  const prefRef = channelNotificationPrefDocRef(baseKey, channelKey, userKey);
  const legacyRef = channelFollowDocRef(baseKey, channelKey, userKey);
  if (!prefRef || !legacyRef || typeof callback !== 'function') {
    return () => {};
  }

  let prefExists = false;
  let prefMode = DEFAULT_CHANNEL_NOTIFICATION_MODE;
  let legacyExists = false;

  const emit = () => {
    if (prefExists) {
      callback(normalizeChannelNotificationMode(prefMode));
      return;
    }
    if (legacyExists) {
      callback(CHANNEL_NOTIFICATION_MODES.ALL);
      return;
    }
    callback(DEFAULT_CHANNEL_NOTIFICATION_MODE);
  };

  const unsubPref = onSnapshot(
    prefRef,
    (snap) => {
      prefExists = Boolean(snap?.exists());
      prefMode = snap?.exists()
        ? snap.data()?.mode
        : DEFAULT_CHANNEL_NOTIFICATION_MODE;
      emit();
    },
    (error) => {
      if (typeof onError === 'function') onError(error);
      else console.error('[ChannelNotificationPrefService] pref subscribe error:', error);
      prefExists = false;
      emit();
    }
  );

  const unsubLegacy = onSnapshot(
    legacyRef,
    (snap) => {
      legacyExists = Boolean(snap?.exists());
      emit();
    },
    (error) => {
      if (typeof onError === 'function') onError(error);
      legacyExists = false;
      emit();
    }
  );

  return () => {
    unsubPref();
    unsubLegacy();
  };
}
